import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Loader2, Check, RefreshCw } from "lucide-react";
import { getWhatsappStatus, linkWhatsapp, unlinkWhatsapp, formatApiErrorDetail } from "../lib/api";

export const WhatsAppCard = () => {
  const [status, setStatus] = useState(null);
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => getWhatsappStatus().then(setStatus).catch(() => {});
  useEffect(() => { load(); }, []);

  const link = async () => {
    setBusy(true);
    try {
      setStatus(await linkWhatsapp(number));
      toast.success("Almost there. Send the code from WhatsApp to confirm.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start linking.");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      setStatus(await unlinkWhatsapp());
      setNumber("");
      toast.success("WhatsApp capture turned off.");
    } catch (_) {
      toast.error("Couldn't unlink.");
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    await load();
    setBusy(false);
  };

  if (!status) return null;

  const wrap = "rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6";
  const heading = (
    <div className="flex items-center gap-2 mb-4">
      <MessageCircle size={16} className="text-[var(--pine)]" strokeWidth={1.9} />
      <h2 className="text-sm uppercase tracking-[0.18em] font-bold text-[var(--ink3)]">WhatsApp capture</h2>
    </div>
  );

  // Internal-only: when not switched on for this account, render nothing at all.
  if (!status.configured) return null;

  if (status.verified) {
    return (
      <div className={wrap} data-testid="whatsapp-card">
        {heading}
        <div className="flex items-center gap-2 mb-1" data-testid="whatsapp-verified">
          <span className="h-6 w-6 rounded-full bg-[var(--pine-soft)] flex items-center justify-center">
            <Check size={14} className="text-[var(--pine)]" strokeWidth={2.4} />
          </span>
          <p className="text-[var(--ink)] font-medium">On for {status.number}</p>
        </div>
        <p className="text-sm text-[var(--ink3)] mb-4">
          Message a thought to {status.bot_number} on WhatsApp and I'll hold it, just like here.
        </p>
        <button
          data-testid="whatsapp-unlink-btn"
          onClick={unlink}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--pine)] hover:border-[var(--pine)] transition-all duration-200 disabled:opacity-50"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Turn off
        </button>
      </div>
    );
  }

  if (status.linked && status.link_code) {
    return (
      <div className={wrap} data-testid="whatsapp-card">
        {heading}
        <p className="text-sm text-[var(--ink3)] mb-3" data-testid="whatsapp-pending">
          One more step to link {status.number}. From WhatsApp:
        </p>
        <ol className="text-sm text-[var(--ink2)] space-y-2 mb-4 list-decimal list-inside">
          {status.sandbox_join && (
            <li>
              Send <span className="font-semibold">{status.sandbox_join}</span> to{" "}
              <span className="font-semibold">{status.bot_number}</span> to start the chat.
            </li>
          )}
          <li>
            Then send this code to confirm it's you:
            <div className="mt-2">
              <span data-testid="whatsapp-link-code" className="rounded-xl bg-[var(--line2)] border border-[var(--line)] px-4 py-2 text-lg font-bold tracking-[0.3em] text-[var(--ink)]">
                {status.link_code}
              </span>
            </div>
          </li>
        </ol>
        <div className="flex items-center gap-2">
          <button
            data-testid="whatsapp-refresh-btn"
            onClick={refresh}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-4 py-2 text-sm transition-all duration-200 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} strokeWidth={2} />}
            I've sent it
          </button>
          <button
            data-testid="whatsapp-unlink-btn"
            onClick={unlink}
            disabled={busy}
            className="rounded-full px-4 py-2 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={wrap} data-testid="whatsapp-card">
      {heading}
      <p className="text-sm text-[var(--ink3)] mb-3">
        Capture from anywhere. Link your number, then message a thought to hold it.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          data-testid="whatsapp-number-input"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="+447700900123"
          className="flex-1 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-[var(--ink)] outline-none focus:border-[var(--pine)] focus:ring-2 focus:ring-[var(--pine-soft)] transition-all duration-200 placeholder:text-[var(--ink5)]"
        />
        <button
          data-testid="whatsapp-link-btn"
          onClick={link}
          disabled={busy || !number.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--pine)] hover:bg-[var(--pine)] disabled:opacity-50 text-[var(--on-pine)] font-semibold px-5 py-2.5 text-sm transition-all duration-200"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          Link WhatsApp
        </button>
      </div>
    </div>
  );
};
